import { useParams, useNavigate } from 'react-router-dom'
import { useEffect, useState, useMemo, useCallback } from 'react'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'

import InvoicesService from '@/services/InvoicesService'
import SettingsService from '@/services/SettingsService'
import Loading from '@/widgets/Loading.jsx'
import ProductTable from '../common/ProductTable'
import AssignPlacowkaModal from '../common/AssignPlacowkaModal'
import AssignProductModal from '../common/AssignProductModal'
import InvoiceHeader from './InvoiceHeader'
import InvoiceSummary from './InvoiceSummary'
import InvoicePlacowkaSection from './InvoicePlacowkaSection'
import InvoiceInfaktSection from './InvoiceInfaktSection'

// ---- helpers (pure, outside component) ----
const dec = v => {
	if (v == null) return 0
	if (typeof v === 'object' && v.$numberDecimal !== undefined) {
		const n = Number(v.$numberDecimal)
		return Number.isFinite(n) ? n : 0
	}
	const n = Number(v)
	return Number.isFinite(n) ? n : 0
}
const NOOP = () => { }
const LOAD_FAILED_KEY = 'invoices.details.messages.loadFailed'

const getPlacowkaId = invoice => {
	const customer = invoice?.customerId
	return (customer?._id || customer || '').toString().trim()
}

const getPlacowkaName = invoice => {
	const customer = invoice?.customerId || {}
	return (invoice?.customerName || customer?.nazwa || customer?.name || '').trim()
}

// Normalize products for read-only ProductTable
const mapProducts = (list = []) =>
	list.map(p => ({
		name: p?.name ?? '—',
		price: dec(p?.price),
		count: dec(p?.count),
		taxPercent: dec(p?.taxPercent),
		taxValue: dec(p?.taxValue),
		total: dec(p?.total),
		productId: p?.productId || '',
	}))

export default function InvoiceDetails() {
	const { id } = useParams()
	const navigate = useNavigate()
	const { t } = useTranslation()

	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [invoice, setInvoice] = useState(null)
	const [deleting, setDeleting] = useState(false)
	const [pushing, setPushing] = useState(false)
	const [currency, setCurrency] = useState('AED') // Default fallback
	const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
	const [isAssigning, setIsAssigning] = useState(false)
	const [assignProductIndex, setAssignProductIndex] = useState(null)
	const [isAssigningProduct, setIsAssigningProduct] = useState(false)

	// Create fmtMoney function with current currency
	const fmtMoney = useCallback((n) => `${currency} ${dec(n).toFixed(2)}`, [currency])

	const loadInvoice = useCallback(async () => {
		setLoading(true)
		setError(null)
		try {
			const { data, error: loadError } = await InvoicesService.getInvoiceById(id)
			if (loadError) setError(loadError)
			else setInvoice(data)
		} catch (err) {
			setError(err.message || LOAD_FAILED_KEY)
		} finally {
			setLoading(false)
		}
	}, [id])

	// Fetch currency from system settings
	useEffect(() => {
		const fetchCurrency = async () => {
			try {
				const { data, error } = await SettingsService.getSystem()
				if (error) {
					console.warn('Failed to load currency from settings:', error)
					return
				}
				if (data?.companyCurrencySymbol) {
					setCurrency(data.companyCurrencySymbol)
				}
			} catch (err) {
				console.warn('Error fetching currency from settings:', err)
			}
		}
		fetchCurrency()
	}, [])

	// Fetch invoice (protect against late setState)
	useEffect(() => {
		let alive = true
			; (async () => {
				setLoading(true)
				setError(null)
				try {
					const { data, error } = await InvoicesService.getInvoiceById(id)
					if (!alive) return
					if (error) setError(error)
					else setInvoice(data)
				} catch (err) {
					if (!alive) return
					setError(err.message || LOAD_FAILED_KEY)
				} finally {
					if (alive) setLoading(false)
				}
			})()
		return () => {
			alive = false
		}
	}, [id])

	// Derived (memoized) totals and rows
	const totals = useMemo(() => {
		if (!invoice) return { taxTotal: 0, discount: 0, total: 0 }
		return {
			taxTotal: dec(invoice.taxTotal),
			subtotal: dec(invoice.subtotal),
			discountPercent: dec(invoice.discountPercent),
			total: dec(invoice.total),
		}
	}, [invoice])

	const productRows = useMemo(() => mapProducts(invoice?.products), [invoice?.products])

	const placowkaId = getPlacowkaId(invoice)
	const placowkaName = getPlacowkaName(invoice)

	const openAssignModal = useCallback(() => {
		if (isAssigning) return
		setIsAssignModalOpen(true)
	}, [isAssigning])

	const closeAssignModal = useCallback(() => {
		if (isAssigning) return
		setIsAssignModalOpen(false)
	}, [isAssigning])

	const handleAssignPlacowka = useCallback(
		async placowka => {
			if (isAssigning || !invoice?._id) return

			const selectedPlacowkaId = placowka?._id || placowka?.id
			if (!selectedPlacowkaId) {
				await Swal.fire({
					title: t('invoices.list.assignPlacowkaModal.errorTitle'),
					text: t('invoices.list.assignPlacowkaModal.invalidPlacowka'),
					icon: 'error',
				})
				return
			}

			setIsAssigning(true)
			try {
				const { success, error: assignError } = await InvoicesService.assignCustomer(
					invoice._id,
					selectedPlacowkaId
				)

				if (success) {
					setIsAssignModalOpen(false)
					await Swal.fire({
						title: t('invoices.list.assignPlacowkaModal.successTitle'),
						text: t('invoices.list.assignPlacowkaModal.successText'),
						icon: 'success',
						timer: 1200,
						showConfirmButton: false,
					})
					await loadInvoice()
				} else {
					await Swal.fire({
						title: t('invoices.list.assignPlacowkaModal.errorTitle'),
						text: assignError || t('invoices.list.assignPlacowkaModal.errorFallback'),
						icon: 'error',
					})
				}
			} catch (err) {
				console.error('Assign placowka error:', err)
				await Swal.fire({
					title: t('invoices.list.assignPlacowkaModal.errorTitle'),
					text: err?.message || t('invoices.list.assignPlacowkaModal.errorFallback'),
					icon: 'error',
				})
			} finally {
				setIsAssigning(false)
			}
		},
		[invoice?._id, isAssigning, loadInvoice, t]
	)

	const openAssignProductModal = useCallback(
		productIndex => {
			if (isAssigningProduct) return
			setAssignProductIndex(productIndex)
		},
		[isAssigningProduct]
	)

	const closeAssignProductModal = useCallback(() => {
		if (isAssigningProduct) return
		setAssignProductIndex(null)
	}, [isAssigningProduct])

	const handleAssignProduct = useCallback(
		async product => {
			if (isAssigningProduct || !invoice?._id || assignProductIndex === null) return

			const selectedProductId = product?._id || product?.id
			if (!selectedProductId) {
				await Swal.fire({
					title: t('invoices.details.assignProductModal.errorTitle'),
					text: t('invoices.details.assignProductModal.invalidProduct'),
					icon: 'error',
				})
				return
			}

			setIsAssigningProduct(true)
			try {
				const { success, error: assignError } = await InvoicesService.assignProduct(
					invoice._id,
					assignProductIndex,
					selectedProductId
				)

				if (success) {
					setAssignProductIndex(null)
					await Swal.fire({
						title: t('invoices.details.assignProductModal.successTitle'),
						text: t('invoices.details.assignProductModal.successText'),
						icon: 'success',
						timer: 1200,
						showConfirmButton: false,
					})
					await loadInvoice()
				} else {
					await Swal.fire({
						title: t('invoices.details.assignProductModal.errorTitle'),
						text: assignError || t('invoices.details.assignProductModal.errorFallback'),
						icon: 'error',
					})
				}
			} catch (err) {
				console.error('Assign product error:', err)
				await Swal.fire({
					title: t('invoices.details.assignProductModal.errorTitle'),
					text: err?.message || t('invoices.details.assignProductModal.errorFallback'),
					icon: 'error',
				})
			} finally {
				setIsAssigningProduct(false)
			}
		},
		[assignProductIndex, invoice?._id, isAssigningProduct, loadInvoice, t]
	)

	const hasInfaktInvoice = Boolean(invoice?.infaktInvoiceUuid || invoice?.infakt_invoice_uuid)

	// Delete flow with guard + toasts
	const handleDelete = useCallback(async () => {
		if (!invoice?._id) return
		if (invoice?.infaktInvoiceUuid || invoice?.infakt_invoice_uuid) return

		const result = await Swal.fire({
			title: t('invoices.details.deleteDialog.confirmTitle'),
			text: t('invoices.details.deleteDialog.confirmText', { number: invoice.number ?? '' }),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#d33',
			cancelButtonColor: '#3085d6',
			confirmButtonText: t('invoices.list.deleteDialog.confirmButton'),
			cancelButtonText: t('invoices.list.deleteDialog.cancelButton'),
		})
		if (!result.isConfirmed) return

		setDeleting(true)
		try {
			const { success, error } = await InvoicesService.deleteInvoices([invoice._id])
			if (success) {
				const msg = t('invoices.details.messages.deletedSuccess', { number: invoice.number ?? '' })
				navigate('/ksiegowosc/invoices', { state: { message: msg } })
			} else {
				await Swal.fire({
					title: t('invoices.list.deleteDialog.errorTitle'),
					text: error || t('invoices.details.messages.deleteFailed'),
					icon: 'error',
				})
			}
		} catch (err) {
			await Swal.fire({
				title: t('invoices.list.deleteDialog.errorTitle'),
				text: err.message || t('invoices.details.messages.unexpectedDeleteError'),
				icon: 'error',
			})
		} finally {
			setDeleting(false)
		}
	}, [invoice?._id, invoice?.number, invoice?.infaktInvoiceUuid, invoice?.infakt_invoice_uuid, navigate, t])

	const handlePushToInfakt = useCallback(async () => {
		if (!invoice?._id || pushing) return
		if (invoice?.infaktInvoiceUuid || invoice?.infakt_invoice_uuid) return

		const result = await Swal.fire({
			title: t('invoices.details.pushToInfaktDialog.confirmTitle'),
			text: t('invoices.details.pushToInfaktDialog.confirmText', { number: invoice.number ?? '' }),
			icon: 'question',
			showCancelButton: true,
			confirmButtonColor: '#059669',
			cancelButtonColor: '#6b7280',
			confirmButtonText: t('invoices.details.pushToInfaktDialog.confirmButton'),
			cancelButtonText: t('invoices.details.pushToInfaktDialog.cancelButton'),
		})
		if (!result.isConfirmed) return

		setPushing(true)
		try {
			const { success, error: pushError } = await InvoicesService.pushToInfakt(invoice._id)
			if (success) {
				await Swal.fire({
					title: t('invoices.details.pushToInfaktDialog.successTitle'),
					text: t('invoices.details.pushToInfaktDialog.successText', { number: invoice.number ?? '' }),
					icon: 'success',
					timer: 1500,
					showConfirmButton: false,
				})
				await loadInvoice()
			} else {
				await Swal.fire({
					title: t('invoices.details.pushToInfaktDialog.errorTitle'),
					text: pushError || t('invoices.details.pushToInfaktDialog.errorFallback'),
					icon: 'error',
				})
			}
		} catch (err) {
			await Swal.fire({
				title: t('invoices.details.pushToInfaktDialog.errorTitle'),
				text: err?.message || t('invoices.details.pushToInfaktDialog.errorFallback'),
				icon: 'error',
			})
		} finally {
			setPushing(false)
		}
	}, [invoice?._id, invoice?.number, invoice?.infaktInvoiceUuid, invoice?.infakt_invoice_uuid, loadInvoice, pushing, t])

	if (loading) return <Loading />
	if (error) return <div className="min-h-screen flex items-center justify-center">{t(error, { defaultValue: error })}</div>
	if (!invoice) return <div className="min-h-screen flex items-center justify-center">{t('invoices.details.noData')}</div>

	return (
		<div className="w-full px-4 sm:px-6 lg:px-8 py-6">
			<InvoiceHeader
				invoice={invoice}
				customerName={placowkaName || t('invoices.list.table.assignPlacowka')}
				placowkaId={placowkaId}
				onAssignClick={openAssignModal}
				onEdit={() => {
					if (hasInfaktInvoice) return
					navigate(`/ksiegowosc/invoices/edit/${invoice._id}`)
				}}
				onDelete={handleDelete}
				onPushToInfakt={handlePushToInfakt}
				onCreateKorekta={() => {
					if (!hasInfaktInvoice) return
					navigate(`/ksiegowosc/korekty/add?invoiceId=${invoice._id}`)
				}}
				deleting={deleting}
				pushing={pushing}
			/>

			<div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(260px,22rem)_minmax(0,1fr)]">

				<InvoiceSummary
					invoice={invoice}
					totals={totals}
					currencySymbol={currency}
					className="flex"
				/>

				<ProductTable
					products={productRows}
					fmtMoney={fmtMoney}
					readOnly
					setProduct={NOOP}
					addRow={NOOP}
					removeRow={NOOP}
					className="flex min-w-0"
					onAssignProduct={openAssignProductModal}
				/>
				
			</div>

			<InvoiceInfaktSection invoice={invoice} />

			<InvoicePlacowkaSection
				placowkaId={placowkaId}
				placowkaName={placowkaName}
				infaktClientCompanyName={invoice?.infakt_client_company_name}
				onAssignClick={openAssignModal}
			/>

			<AssignPlacowkaModal
				isOpen={!!isAssignModalOpen}
				onClose={closeAssignModal}
				onSelect={handleAssignPlacowka}
				isAssigning={isAssigning}
			/>

			<AssignProductModal
				isOpen={assignProductIndex !== null}
				onClose={closeAssignProductModal}
				onSelect={handleAssignProduct}
				isAssigning={isAssigningProduct}
				infaktProductName={
					assignProductIndex !== null ? productRows[assignProductIndex]?.name || '' : ''
				}
				price={assignProductIndex !== null ? productRows[assignProductIndex]?.price ?? '' : ''}
				taxPercent={
					assignProductIndex !== null ? productRows[assignProductIndex]?.taxPercent ?? '' : ''
				}
			/>
		</div>
	)
}
